import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AddNewModelTask from "./AddNewModelTask";
import TabTitle from "components/Elements/TabTitle";
import HistoryBar from "components/Modules/HistorySidebar/HistoryBar";
import ModelTaskWorkspace from "components/Modules/ModelTaskWorkspace";
import createModelTaskColumns from "components/Modules/ModelTaskWorkspace/modelTaskColumns";
import { ModelContext } from "contexts/ModelDetailContext";
import { coalesc } from "helpers/utils";
import { HistoryCaptions } from "helpers/constants";
import { TasksPage } from "services/History/models";
import { addModelTask } from "services/models/modelDetails/modelTasks";
import { Apis } from "services/api";
import useSiteApplicationDetail from "hooks/useSiteApplicationDetail";
import { parseExpandedTasks } from "helpers/modelTaskUrlState";
import { setCopiedQuestionsTaskId } from "helpers/modelTaskClipboard";
import useModelTaskScreenController from "./hooks/useModelTaskScreenController";
import useTaskCopyPaste from "./hooks/useTaskCopyPaste";
import withMount from "components/HOC/withMount";

const getCurrentUser = () =>
	JSON.parse(sessionStorage.getItem("me")) || JSON.parse(localStorage.getItem("me"));

// Navigation from the Service Layout arrives in router state; a refresh or a
// shared link arrives in the query string. Both end up as the same shape.
const getInitialExpansion = (location) => {
	const fromState =
		location.state?.modelVersionTaskID || location.state?.ModelVersionTaskID;
	if (fromState) return { expandedIds: [fromState], primaryId: fromState };
	return parseExpandedTasks(location.search);
};

function Task({
	modelId,
	state,
	dispatch,
	access,
	getCurrentTaskTableSort,
	isTaskListImportSuccess,
}) {
	const location = useLocation();
	const { t } = useTranslation();
	const initialExpansion = useMemo(
		// Read once on mount: this seeds the table, which then owns the URL.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		() => getInitialExpansion(location),
		[]
	);
	const controller = useModelTaskScreenController({
		modelId,
		anchorTaskId: initialExpansion.primaryId,
	});
	const { refresh, sorting, totalCount } = controller;
	const [{ modelDetail }] = useContext(ModelContext);
	const currentUser = useMemo(getCurrentUser, []);
	const { position, application, customCaptions, siteAppID } = currentUser;
	const siteApplication = useSiteApplicationDetail(siteAppID)?.data;

	useEffect(() => {
		if (isTaskListImportSuccess) refresh();
	}, [isTaskListImportSuccess, refresh]);

	useEffect(() => {
		const selected = sorting[0];
		if (selected) {
			getCurrentTaskTableSort?.([
				selected.id,
				selected.desc ? "desc" : "asc",
			]);
		}
	}, [getCurrentTaskTableSort, sorting]);

	const { handleCopy } = useTaskCopyPaste({
		modelId,
		refresh,
		showPasteTask: state.showPasteTask,
		dispatch,
	});

	const createTask = useCallback(
		(payload) => addModelTask({ ...payload, modelVersionID: modelId }),
		[modelId]
	);
	const handleDelete = useCallback(async () => {
		await refresh();
	}, [refresh]);
	const handleCopyTaskQuestion = useCallback((modelTaskId) => {
		navigator.clipboard.writeText(modelTaskId);
		setCopiedQuestionsTaskId(modelTaskId);
		dispatch({ type: "DISABLE_QUESTIONS_TASKS", payload: false });
	}, [dispatch]);
	const tableConfiguration = useMemo(
		() =>
			createModelTaskColumns({
				showOperatingMode: siteApplication?.application?.showOperatingMode,
				showSystem: siteApplication?.application?.showSystem,
				customCaptions,
				modelType: modelDetail?.modelType,
			}),
		[customCaptions, modelDetail?.modelType, siteApplication]
	);

	return (
		<div>
			<TabTitle
				title={`${modelDetail?.name} ${coalesc(modelDetail?.modelName)} ${customCaptions.taskPlural} | ${application.name}`}
			/>
			<HistoryBar
				id={modelId}
				showhistorybar={state.showhistorybar}
				dispatch={dispatch}
				fetchdata={(id, pageNumber, pageSize) => TasksPage(id, pageNumber, pageSize)}
				OnAddItemClick={() => {}}
				hasSubTable
				origin={HistoryCaptions.modelVersionTasks}
			/>
			<AddNewModelTask
				open={state.showAdd}
				closeHandler={() => dispatch({ type: "TOGGLE_ADD", payload: false })}
				siteId={position?.siteAppID}
				data={null}
				title={t("action.addEntity", { entity: customCaptions.task })}
				modelId={modelId}
				createProcessHandler={createTask}
				fetchData={refresh}
				customCaptions={customCaptions}
				totalTaskCount={totalCount}
				showSave
			/>
			<ModelTaskWorkspace
				{...controller}
				onSearchChange={controller.setSearch}
				tableConfiguration={tableConfiguration}
				customCaptions={customCaptions}
				modelId={modelId}
				access={access}
				deleteEndpoint={Apis.ModelTasks}
				onSortingChange={controller.setSorting}
				onDelete={handleDelete}
				onCopy={handleCopy}
				onCopyTaskQuestion={handleCopyTaskQuestion}
				onRefresh={refresh}
				initialExpandedIds={initialExpansion.expandedIds}
				initialPrimaryId={initialExpansion.primaryId}
			/>
		</div>
	);
}

export default withMount(Task);
